import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Container
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import api from '../../services/api';

interface PaymentSettings {
  id?: number;
  active_provider: string;
  pagarme_enabled: boolean;
  pagarme_configured: boolean;
  mercadopago_enabled: boolean;
  mercadopago_configured: boolean;
  created_at?: string;
  updated_at?: string;
  updated_by?: string;
}

interface ProviderStatus {
  active_provider: string;
  pagarme: {
    configured: boolean;
    enabled: boolean;
    is_active: boolean;
  };
  mercadopago: {
    configured: boolean;
    enabled: boolean;
    is_active: boolean;
  };
}

interface PaymentProviderInfo {
  active_provider: string;
  available_providers: string[];
  providers_status: {
    pagarme: {
      enabled: boolean;
      configured: boolean;
      methods: string[];
    };
    mercadopago: {
      enabled: boolean;
      configured: boolean;
      methods: string[];
    };
  };
}

const PaymentSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [providerStatus, setProviderStatus] = useState<ProviderStatus | null>(null);
  const [providerInfo, setProviderInfo] = useState<PaymentProviderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [configDialog, setConfigDialog] = useState<{ open: boolean; provider: 'pagarme' | 'mercadopago' | null }>({
    open: false,
    provider: null
  });

  // Estados para configurações
  const [formData, setFormData] = useState({
    active_provider: 'pagarme',
    pagarme_secret_key: '',
    pagarme_public_key: '',
    pagarme_account_id: '',
    pagarme_enabled: true,
    mercadopago_access_token: '',
    mercadopago_public_key: '',
    mercadopago_user_id: '',
    mercadopago_enabled: false
  });

  useEffect(() => {
    loadSettings();
    loadProviderStatus();
    loadProviderInfo();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payment-settings/');
      if (response.data.success) {
        const data = response.data.data;
        setSettings(data);
        setFormData({
          active_provider: data.active_provider || 'pagarme',
          pagarme_secret_key: '',
          pagarme_public_key: '',
          pagarme_account_id: '',
          pagarme_enabled: data.pagarme_enabled ?? true,
          mercadopago_access_token: '',
          mercadopago_public_key: '',
          mercadopago_user_id: '',
          mercadopago_enabled: data.mercadopago_enabled ?? false
        });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: `Erro ao carregar configurações: ${error.response?.data?.detail || error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const loadProviderStatus = async () => {
    try {
      const response = await api.get('/payment-settings/status');
      if (response.data.success) {
        setProviderStatus(response.data.data);
      }
    } catch (error: any) {
      console.error('Erro ao carregar status dos provedores:', error);
    }
  };

  const loadProviderInfo = async () => {
    try {
      const response = await api.get('/usage/provider-info');
      if (response.data.success) {
        setProviderInfo(response.data.data);
      }
    } catch (error: any) {
      console.error('Erro ao carregar info dos provedores:', error);
    }
  };

  const handleSwitchProvider = async (provider: string) => {
    try {
      setSaving(true);
      setMessage(null);

      const response = await api.post('/payment-settings/switch-provider', {
        active_provider: provider
      });

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `Provedor alterado para ${provider} com sucesso!`
        });
        await loadSettings();
        await loadProviderStatus();
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: `Erro ao trocar provedor: ${error.response?.data?.detail || error.message}`
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfiguration = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const response = await api.put('/payment-settings/', formData);

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Configurações salvas com sucesso!'
        });
        await loadSettings();
        await loadProviderStatus();
        setConfigDialog({ open: false, provider: null });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: `Erro ao salvar configurações: ${error.response?.data?.detail || error.message}`
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (provider: 'pagarme' | 'mercadopago') => {
    try {
      setTesting(provider);
      setMessage(null);

      const response = await api.post(`/payment-settings/test-connection/${provider}`);

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `Conexão com ${provider} testada com sucesso!`
        });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: `Erro ao testar ${provider}: ${error.response?.data?.detail || error.message}`
      });
    } finally {
      setTesting(null);
    }
  };

  const handleRefresh = () => {
    loadSettings();
    loadProviderStatus();
    loadProviderInfo();
  };

  const getProviderIcon = (provider: string, isActive: boolean) => {
    const iconColor = isActive ? 'success' : 'disabled';
    return <PaymentIcon color={iconColor} />;
  };

  const getStatusChip = (configured: boolean, enabled: boolean, isActive: boolean) => {
    if (isActive) {
      return <Chip label="Ativo" color="success" size="small" />;
    }
    if (configured && enabled) {
      return <Chip label="Configurado" color="primary" size="small" />;
    }
    if (configured && !enabled) {
      return <Chip label="Desabilitado" color="warning" size="small" />;
    }
    return <Chip label="Não Configurado" color="error" size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Configurações de Pagamento
          </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading || saving}
        >
          Atualizar
        </Button>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Status dos Provedores */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Status dos Provedores de Pagamento
              </Typography>
              
              {providerStatus && (
                <List>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          {getProviderIcon('pagarme', providerStatus.pagarme.is_active)}
                          <Typography sx={{ ml: 1 }} variant="subtitle1">
                            Pagarme
                          </Typography>
                        </Box>
                      }
                      secondary={`Métodos: PIX, Cartão de Crédito`}
                    />
                    <ListItemSecondaryAction>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusChip(
                          providerStatus.pagarme.configured,
                          providerStatus.pagarme.enabled,
                          providerStatus.pagarme.is_active
                        )}
                        <Tooltip title="Configurar Pagarme">
                          <IconButton
                            size="small"
                            onClick={() => setConfigDialog({ open: true, provider: 'pagarme' })}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                        <Button
                          size="small"
                          onClick={() => handleTestConnection('pagarme')}
                          disabled={!providerStatus.pagarme.configured || testing === 'pagarme'}
                        >
                          {testing === 'pagarme' ? <CircularProgress size={16} /> : 'Testar'}
                        </Button>
                        {!providerStatus.pagarme.is_active && providerStatus.pagarme.configured && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSwitchProvider('pagarme')}
                            disabled={saving}
                          >
                            Ativar
                          </Button>
                        )}
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  
                  <Divider />
                  
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          {getProviderIcon('mercadopago', providerStatus.mercadopago.is_active)}
                          <Typography sx={{ ml: 1 }} variant="subtitle1">
                            MercadoPago
                          </Typography>
                        </Box>
                      }
                      secondary={`Métodos: Checkout Pro, Cartão de Crédito`}
                    />
                    <ListItemSecondaryAction>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusChip(
                          providerStatus.mercadopago.configured,
                          providerStatus.mercadopago.enabled,
                          providerStatus.mercadopago.is_active
                        )}
                        <Tooltip title="Configurar MercadoPago">
                          <IconButton
                            size="small"
                            onClick={() => setConfigDialog({ open: true, provider: 'mercadopago' })}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                        <Button
                          size="small"
                          onClick={() => handleTestConnection('mercadopago')}
                          disabled={!providerStatus.mercadopago.configured || testing === 'mercadopago'}
                        >
                          {testing === 'mercadopago' ? <CircularProgress size={16} /> : 'Testar'}
                        </Button>
                        {!providerStatus.mercadopago.is_active && providerStatus.mercadopago.configured && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSwitchProvider('mercadopago')}
                            disabled={saving}
                          >
                            Ativar
                          </Button>
                        )}
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Informações Adicionais */}
        {providerInfo && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Informações do Sistema
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Provedor Ativo:</strong> {providerInfo.active_provider}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Provedores Disponíveis:</strong> {providerInfo.available_providers.join(', ')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Dialog de Configuração */}
      <Dialog 
        open={configDialog.open} 
        onClose={() => setConfigDialog({ open: false, provider: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Configurar {configDialog.provider === 'pagarme' ? 'Pagarme' : 'MercadoPago'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {configDialog.provider === 'pagarme' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.pagarme_enabled}
                        onChange={(e) => setFormData({ ...formData, pagarme_enabled: e.target.checked })}
                      />
                    }
                    label="Habilitar Pagarme"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Secret Key"
                    type="password"
                    value={formData.pagarme_secret_key}
                    onChange={(e) => setFormData({ ...formData, pagarme_secret_key: e.target.value })}
                    placeholder="sk_test_..."
                    helperText="Chave secreta do Pagarme"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Public Key"
                    value={formData.pagarme_public_key}
                    onChange={(e) => setFormData({ ...formData, pagarme_public_key: e.target.value })}
                    placeholder="pk_test_..."
                    helperText="Chave pública do Pagarme"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Account ID"
                    value={formData.pagarme_account_id}
                    onChange={(e) => setFormData({ ...formData, pagarme_account_id: e.target.value })}
                    placeholder="acc_..."
                    helperText="ID da conta do Pagarme"
                  />
                </Grid>
              </Grid>
            )}

            {configDialog.provider === 'mercadopago' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.mercadopago_enabled}
                        onChange={(e) => setFormData({ ...formData, mercadopago_enabled: e.target.checked })}
                      />
                    }
                    label="Habilitar MercadoPago"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Access Token"
                    type="password"
                    value={formData.mercadopago_access_token}
                    onChange={(e) => setFormData({ ...formData, mercadopago_access_token: e.target.value })}
                    placeholder="TEST-..."
                    helperText="Access Token do MercadoPago"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Public Key"
                    value={formData.mercadopago_public_key}
                    onChange={(e) => setFormData({ ...formData, mercadopago_public_key: e.target.value })}
                    placeholder="TEST-..."
                    helperText="Public Key do MercadoPago"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="User ID"
                    value={formData.mercadopago_user_id}
                    onChange={(e) => setFormData({ ...formData, mercadopago_user_id: e.target.value })}
                    placeholder="2570784001"
                    helperText="ID do usuário MercadoPago"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Dados de teste fornecidos:</strong><br />
                      User ID: 2570784001<br />
                      Public Key: TEST-cbcafe68-968f-4762-aef5-150bd40419e1<br />
                      Access Token: TEST-2209749166769140-072117-28813675a3c4c5f1952cba1560fc9dcb-2570784001<br /><br />
                      <strong>Cartão de teste:</strong><br />
                      Número: 5031 4332 1540 6351<br />
                      Validade: 11/30<br />
                      CVC: 123
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog({ open: false, provider: null })}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveConfiguration}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            Salvar Configurações
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
  );
};

export default PaymentSettingsPage; 